import { HL7MappingListDto, QueryFactory } from '@lmt/shared-types';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MappingTable } from './components/MappingTable';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './HL7MappingPage.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import { useEventCallback } from '@mui/material';

export const HL7MappingPage: FC = React.memo(function MappingPage() {
  const mappingId = pages.hl7MappingsWithId.useMatch()?.params?.mappingId;
  const tabs = useTabs();
  const i18n = useTranslation();
  const i18next = useScopedTranslation('hl7.mapping.list');
  const modals = useModal();
  const { data: mappings, isLoading: isLoading } =
    QueryFactory.HL7MappingQuery.useGetAllQuery();
  const queryCache = useQueryClient();

  const [activeMappingId, setActiveMappingId] = useState<
    number | undefined | null
  >(mappingId);
  useEffect(() => {
    if (mappings && mappings.length > 0 && !activeMappingId) {
      setActiveMappingId(mappings[0].id);
    }
  }, [activeMappingId, mappings]);

  useEffect(() => {
    if (mappings && mappings.length > 0 && !activeMappingId) {
      setActiveMappingId(mappings[0].id);
    }
  }, [mappings]);

  useEffect(() => {
    if (activeMappingId) {
      tabs.changeTabPath(
        pages.hl7MappingsWithId.link({
          mappingId: activeMappingId,
        }),
        true,
      );
    }
  }, [activeMappingId]);

  const [key, setKey] = useState(1);
  const closeDropdown = useCallback(() => {
    setKey((key) => ++key);
  }, []);

  const onMappingAdd = useEventCallback(async () => {
    const result = await modals.showPrompt({
      title: i18next.t('add.title'),
      isMandatory: true,
      fieldName: i18next.t('add.text'),
      text: '',
      defaultValue: '',
    });
    if (result) {
      try {
        const createdMapping = await QueryFactory.HL7MappingClient.create({
          name: result,
          rules: [],
        });
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.HL7MappingQuery.getAllQueryKey(),
        });
        setActiveMappingId(createdMapping.id);
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    }
  });

  const onMappingDelete = useEventCallback(
    async (mapping: HL7MappingListDto) => {
      try {
        if (
          await modals.showDeletionConfirmation({
            title: i18next.t('delete.confirm_title'),
            text: i18next.t('delete.confirm_text', { name: mapping.name }),
          })
        ) {
          await QueryFactory.HL7MappingClient.remove(mapping.id);
          if (activeMappingId === mapping.id) {
            setActiveMappingId(
              mappings?.filter((x) => x.id !== mapping.id)?.[0]?.id,
            );
          }
          await queryCache.invalidateQueries({
            queryKey: QueryFactory.HL7MappingQuery.getAllQueryKey(),
          });
        }
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
  );

  const onMappingRename = useEventCallback(
    async (mapping: HL7MappingListDto) => {
      try {
        const newName = await modals.showPrompt({
          title: i18next.t('rename.title'),
          isMandatory: true,
          fieldName: i18next.t('rename.text'),
          text: '',
          defaultValue: mapping.name,
        });
        if (newName) {
          await QueryFactory.HL7MappingClient.patch(mapping.id, {
            name: newName,
          });
          await queryCache.invalidateQueries({
            queryKey: QueryFactory.HL7MappingQuery.getAllQueryKey(),
          });
        }
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
  );

  const renderMappingOption = useEventCallback((mapping: HL7MappingListDto) => {
    return (
      <div className={styles.controls}>
        {!mapping.isDefault && (
          <a
            className={styles.editLink}
            onClick={(e) => {
              e.stopPropagation(); // to prevent onOptionClick
              closeDropdown();
              onMappingRename(mapping);
            }}
          >
            {i18next.t('rename_button')}
          </a>
        )}
        {!mapping.isDefault && (
          <a
            className={styles.deleteLink}
            onClick={(e) => {
              e.stopPropagation(); // to prevent onOptionClick
              closeDropdown();
              onMappingDelete(mapping);
            }}
          >
            {i18next.t('delete_button')}
          </a>
        )}
      </div>
    );
  });

  return (
    <div className={styles.storedScrolling}>
      <Loading title={i18n.t('hl7.mapping.loading')} loading={isLoading}>
        <div className={styles.container} data-test-id="hl7-mapping-page">
          <div className={styles.filterArea}>
            <Field
              title={i18n.t('hl7.mapping.mappings_drop_down_title')}
              className={styles.filterField}
              linkProps={{
                title: i18next.t('add_button'),
                onClick: () => onMappingAdd(),
                className: styles.edit,
              }}
            >
              <DropDownInput
                key={key}
                useIdFunctionAsValue={true}
                required
                variant="normal"
                getOptionLabel="name"
                idFunction="id"
                options={mappings ?? []}
                value={activeMappingId}
                onValueChanged={(option) => {
                  setActiveMappingId(option!.id);
                }}
                classes={{
                  option: styles.option,
                }}
                postfixRenderer={renderMappingOption}
              />
            </Field>
          </div>
          <div className={styles.table}>
            {mappings &&
              activeMappingId &&
              mappings.find((x) => x.id === activeMappingId) && (
                <MappingTable mappingId={activeMappingId} />
              )}
          </div>
        </div>
      </Loading>
    </div>
  );
});
